import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  IconButton,
} from '@mui/material';
import { ChevronDown as ChevronDownIcon, Trash2 as TrashIcon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { useWorkoutStore } from '../store/workoutStore';
import { useExerciseStore } from '../store/exerciseStore';

const History = () => {
  const history = useWorkoutStore((state) => state.history);
  const deleteWorkout = useWorkoutStore((state) => state.deleteWorkout);
  const exercises = useExerciseStore((state) => state.exercises);
  const [tabValue, setTabValue] = useState(0);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const getExerciseName = (exerciseId) => {
    return exercises.find((e) => e.id === exerciseId)?.name || 'Ejercicio desconocido';
  };

  const getMuscleGroupColor = (group) => {
    const colors = {
      pecho: '#FF6B6B',
      espalda: '#4ECDC4',
      piernas: '#45B7D1',
      brazos: '#FFA07A',
      core: '#DDA15E',
    };
    return colors[group] || '#95E1D3';
  };

  // Filtrar por período
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  const thisWeek = history.filter((w) => new Date(w.date) >= weekAgo);
  const thisMonth = history.filter((w) => new Date(w.date) >= monthAgo);
  const allTime = history;

  const displayedHistory =
    tabValue === 0 ? thisWeek : tabValue === 1 ? thisMonth : allTime;

  if (history.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: 'center',
          bgcolor: 'grey.50',
          borderRadius: 2,
          border: '2px dashed',
          borderColor: 'grey.300',
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          Sin entrenamientos aún
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Completa tu primer entrenamiento para ver el historial aquí
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
          Historial de Entrenamientos
        </Typography>
        <Tabs
          value={tabValue}
          onChange={(e, val) => setTabValue(val)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontSize: '0.9rem',
            },
          }}
        >
          <Tab label={`Esta Semana (${thisWeek.length})`} />
          <Tab label={`Este Mes (${thisMonth.length})`} />
          <Tab label={`Todo (${allTime.length})`} />
        </Tabs>
      </Box>

      {displayedHistory.length === 0 ? (
        <Box
          sx={{
            p: 2,
            textAlign: 'center',
            bgcolor: 'grey.50',
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Sin entrenamientos en este período
          </Typography>
        </Box>
      ) : (
        <List sx={{ gap: 1, display: 'flex', flexDirection: 'column' }}>
          {displayedHistory.map((workout) => {
            const isExpanded = expandedId === workout.id;
            return (
              <ListItem key={workout.id} disablePadding>
                <Card
                  sx={{
                    width: '100%',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    {/* Header */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'start',
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        setExpandedId(isExpanded ? null : workout.id)
                      }
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {new Date(workout.date).toLocaleDateString('es-ES', {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short',
                            })}
                          </Typography>
                          <Chip
                            label="✓ Completado"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {workout.exercises.filter((ex) => ex.sets.some((s) => s.completed)).length} ejercicios •{' '}
                          {workout.duration} min • hace{' '}
                          {formatDistanceToNow(new Date(workout.date), {
                            locale: es,
                          })}
                        </Typography>
                      </Box>
                      <IconButton size="small">
                        <ChevronDownIcon
                          size={20}
                          style={{
                            transform: isExpanded
                              ? 'rotate(180deg)'
                              : 'rotate(0)',
                            transition: 'transform 0.2s',
                          }}
                        />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteWorkout(workout.id);
                        }}
                        color="error"
                      >
                        <TrashIcon size={18} />
                      </IconButton>
                    </Box>

                    {/* Details */}
                    {isExpanded && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        {workout.exercises.filter((ex) => ex.sets.some((s) => s.completed)).map((exercise, excIdx) => {
                          const exerciseName = getExerciseName(exercise.exerciseId);
                          const exerciseData = exercises.find(
                            (e) => e.id === exercise.exerciseId
                          );
                          return (
                            <Box key={excIdx} sx={{ mb: 1.5 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mb: 0.5,
                                }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {exerciseName}
                                </Typography>
                                {exerciseData && (
                                  <Chip
                                    label={exerciseData.muscleGroup}
                                    size="small"
                                    sx={{
                                      bgcolor: getMuscleGroupColor(
                                        exerciseData.muscleGroup
                                      ),
                                      color: 'white',
                                      height: 20,
                                      fontSize: '0.7rem',
                                    }}
                                  />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {exercise.sets
                                  .filter((set) => set.completed)
                                  .map((set, setIdx) => (
                                    <Chip
                                      key={setIdx}
                                      label={`Set ${setIdx + 1}: ${set.reps}x${set.weight}kg`}
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                    />
                                  ))}
                              </Box>
                            </Box>
                          );
                        })}
                        {workout.notes && (
                          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              Notas:
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {workout.notes}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export default History;
